import { FC, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { SuccessModal } from 'src/components/elements/modals'
import { Button, Checkbox, InputField } from 'src/components/ui'
import { isErrorWithDataMessage, isErrorWithMessage, isFetchBaseQueryError } from 'src/helpers'
import { useRegisterMutation } from 'src/redux/api'

interface IRegistrationForm {}

const RegistrationForm: FC<IRegistrationForm> = () => {
  const { t } = useTranslation()
  const [register, { error }] = useRegisterMutation()
  const navigate = useNavigate()
  const [openSuccessModal, setOpenSuccessModal] = useState(false)

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      password: '',
      agreeTerms: false,
    },
  })

  const onSubmit = async (data: any) => {
    try {
      await register(data).unwrap()
      setOpenSuccessModal(true)
      reset()
    } catch (err) {
      if (isFetchBaseQueryError(err)) {
        const errData = err.data as { error: string }
        // you can access all properties of `FetchBaseQueryError` here
        const errMsg = 'error' in err ? err.error : errData.error
        setError('root', { message: errMsg })
      } else if (isErrorWithMessage(err)) {
        // you can access a string 'message' property here
        setError('root', { message: err.message })
      }
    }
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 self-stretch w-full">
        {isErrorWithDataMessage(error) && (
          <p className="m-0 px-3.5 py-2.5 rounded-xl bg-danger-soft text-sm font-bold text-danger">
            {error?.data?.message}
          </p>
        )}
        {errors.root?.message && (
          <p className="m-0 px-3.5 py-2.5 rounded-xl bg-danger-soft text-sm font-bold text-danger">
            {errors.root?.message}
          </p>
        )}
        <div className="grid md:grid-cols-2 gap-4 gap-x-3">
          <Controller
            name="firstName"
            control={control}
            rules={{ required: t('registration_form.validation.required') }}
            render={({ field }) => (
              <InputField
                {...field}
                type="text"
                autoComplete="given-name"
                label={t('registration_form.firstNamePlaceholder')}
                error={errors.firstName}
              />
            )}
          />
          <Controller
            name="lastName"
            control={control}
            rules={{ required: t('registration_form.validation.required') }}
            render={({ field }) => (
              <InputField
                {...field}
                type="text"
                autoComplete="family-name"
                label={t('registration_form.lastNamePlaceholder')}
                error={errors.lastName}
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            rules={{
              required: t('registration_form.validation.required'),
              minLength: { value: 6, message: t('registration_form.validation.passwordMinLength') },
            }}
            render={({ field }) => (
              <InputField
                {...field}
                label={t('registration_form.passwordPlaceholder')}
                type="password"
                autoComplete="new-password"
                error={errors.password}
              />
            )}
          />
        </div>

        <Controller
          name="agreeTerms"
          control={control}
          rules={{
            required: t('registration_form.validation.agreeTerms'),
          }}
          render={({ field }) => (
            // @ts-ignore
            <Checkbox
              {...field}
              label={t('registration_form.agreeTermsLabel')}
              error={errors.agreeTerms?.message}
            />
          )}
        />

        <Button size="lg" className="w-full" type="submit">
          {t('registration_form.submitButton')}
        </Button>
      </form>
      <SuccessModal
        title={t('registration_form.successTitle')}
        text={t('completeRegistration_form.successText')}
        buttonText={t('registration_form.successButton')}
        open={openSuccessModal}
        handleClose={() => {
          navigate('/login')
          setOpenSuccessModal(false)
        }}
      />
    </>
  )
}

export default RegistrationForm
